import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';
import cx from 'classnames';
import { EditorContext } from '../../index';
import ColumnResizer from "react-column-resizer";
import Draggable from 'react-draggable';
import shortid from 'shortid';
import { HtmlView } from '@paperflite-marketing/cleverstory-commons';

import {
  TabContainer,
  Nav,
  Tab,
  Button,
  OverlayTrigger,
  Popover,
  Form,
} from "react-bootstrap";

import { CodeEditor } from "./CodeEditor/index";
import { ResourceEditor } from './ResourceEditor';
import { Property } from '../../components/properties/';
import Portal from 'components/Portal';
import { getParentContainerOfType, isWidgetHasHotspotChild, getWidgetType } from 'components/Editor/helper/widget';
import { useAllowCreateLiveElement } from 'components/Editor/hooks/useEditorSession';

import { toggleEditMode } from 'actions/editor';
import { updateWidgetContent } from 'actions/editor';
import { LiveElementCreationProperty } from 'components/Editor/components/liveElements';

import { GROUP, HOTSPOT, PAGE, SECTION_COLUMN, CONTAINER } from 'constants/widgetTypes';
import { AutoGrowProperty } from 'components/Editor/components/autoGrow';

import styles from './html.module.scss';
import { BrandComponentProperty } from 'components/Editor/components/brandElements';
import userRoleService from 'services/userRoles.service';


const HtmlWidgetComponent = ({ className, style, widgetProps, widgetRef }) => {
  const ref = widgetRef;

  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');

  const html = _.get(widgetProps, 'content.html');
  const empty = !(html && html.trim().length > 0);
  const iframe = _.get(widgetProps, 'properties.desktop.iframe')

  var widgetStyle = {
    ...style,
    "overflow-y":"auto"
  }

  return (
    <div id={id}
      ref={ref}
      className={cx(className, styles.htmlViewer, { [styles.empty]: empty })}
      style={widgetStyle}
      data-widget-id={id}
      data-widget-type={type}
      data-selectable={true}
    >
      <div className={iframe ? styles.htmlViewerInnerIframe : styles.htmlViewerInner}>
        {empty &&
          <div>Please edit your custom html widget</div>
        }
        {!empty &&
          <HtmlView content={widgetProps.content} iframe={iframe} id={id} />
        }
      </div>
    </div>
  )
};

const htmlWidgetEquals = function (prevProps, nextProps) {
  const paths = ['className', 'style', 'widgetProps', 'widgetRef'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

const HtmlWidget = React.memo(HtmlWidgetComponent, htmlWidgetEquals);

const HtmlWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const allowLiveElement = useAllowCreateLiveElement(editorSession, props);
  
  const [view, setView] = useState(null);
  
  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);
  const allowBrandkit = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`)) !== 'PALETTE_COMPONENT';

  const id = _.get(props, 'uid');
  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const template = _.get(props, 'template.widget') != null;

  const widgetContent = _.get(props, `content`);
  const breakpoint = _.get(props, `breakpoint`);
  const iframe = _.get(props, `properties.${breakpoint}.iframe`, false);
  const handleEditMode = () => {
    setView(null);
    dispatch(toggleEditMode(editorSession, props.uid, true));
  }

  const changeView = (view) => {
    if (editMode) {
      dispatch(toggleEditMode(editorSession, props.uid, false));
    }

    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  const handleChangeHtml = (value) => {
    dispatch(updateWidgetContent(editorSession, id,
      {
        ...widgetContent,
        html: value
      }
    ));
  }

  const handleChangeCss = (value) => {
    dispatch(updateWidgetContent(editorSession, id,
      {
        ...widgetContent,
        css: value
      }
    ));
  }

  const handleChangeJs = (value) => {
    dispatch(updateWidgetContent(editorSession, id,
      {
        ...widgetContent,
        script: value
      }
    ));
  }

  return (
    <>
      <Property.Action className={styles.iconButton} tooltip='HTML' onClick={handleEditMode}><i className={'cleverstory-Embed'} /></Property.Action>

      {!template && !template && allowLiveElement && 
        <Property.Action className={styles.iconButton} tooltip='Live Element' onClick={() => changeView('liveElements')}>
          <i className={'cleverstory-live-element'} />
        </Property.Action>
      }
      {!template && brandkitEnabled && allowBrandkit && userRoleService.canManageUsers() &&
        <Property.Action className={styles.iconButton} tooltip='Add to Brand Kit' onClick={() => changeView('brandMedia')}>
          <i className={'cleverstory-brandmedia'} />
        </Property.Action>
      }
      {!iframe && (
        <Property.Action
          className={cx(styles.iconButton)}
          tooltip='Auto Grow'
          onClick={() => changeView('autoGrow')}
        >
          <i className={cx("cleverstory-stretch", styles.rotate90)} />
        </Property.Action>
      )}

      {editMode &&
        <HtmlViewEditor
          id={id}
          editor={editorSession}
          widget={props}
          handleHtml={handleChangeHtml}
          handleCss={handleChangeCss}
          handleJs={handleChangeJs}
          handleSetting={() => changeView('settings')}
          onCancel={() => {
            closePopup()
          }} />
      }

      {!editMode && view === 'settings' &&
        <HtmlSettingDialog
          id={id}
          show={view === 'settings'}
          editor={editorSession}
          widget={props}
          onClose={handleEditMode}
        />
      }

      {!editMode && view === 'liveElements' &&
        <LiveElementCreationProperty elements={[props]} onCancel={() => closePopup()} />
      }
      {view === 'brandMedia' &&
        <BrandComponentProperty type={'EMBED'} widgets={[props]} onCancel={() => closePopup()} />
      }
      {view === 'autoGrow' &&
        <AutoGrowProperty widget={props} onCancel={() => closePopup()} />
      }
    </>
  );
}

const HtmlViewEditor = (props) => {
  const [state, setState] = useState({
    fullView: false,
    active: 'html',
    layout: 'horizontal',
  })

  const id = _.get(props, 'widget.uid');
  const widgetContent = _.get(props, `widget.content`);

  const renderTabsIcon = (title) => {
    return (
      <div className={styles.tabsButton}>
        <i className={`cleverstory-${title}`} />
        {state.active === title && <span className={styles.tabsTitle}>{title}</span>}
      </div>
    )
  }

  const toggleView = () => {
    setState(state => {
      return {
        ...state,
        fullView: !state.fullView,
      }
    })
  }

  const handleTabChange = (active) => {
    setState(state => {
      return {
        ...state,
        active: active
      }
    })
  }

  const handleLayoutChange = (layout) => {
    setState(state => {
      return {
        ...state,
        layout: layout
      }
    })
  }

  const popover = (
    <Popover
      id="popover-arrange-tool"
      className={styles.layoutPopup}
    >
      <Popover.Body>
        <button className={styles.action} onClick={() => handleLayoutChange('vertical-right')}>
          <i className="cleverstory-right-view" />
        </button>
        <button className={styles.action} onClick={() => handleLayoutChange('vertical-left')}>
          <i className="cleverstory-left-view" />
        </button>
        <button className={styles.action} onClick={() => handleLayoutChange('horizontal')}>
          <i className="cleverstory-top-view" />
        </button>
      </Popover.Body>
    </Popover>
  );

  return (
    <HtmlEditorDialog
      width={300}
      height={400}
      className={styles.htmlEditorDialog}
      target={`#${id}`}
      onCancel={props.onCancel}
      fullView={state.fullView}
    >

      <TabContainer id='html-widget-editor-tab' defaultActiveKey={state.active} onSelect={handleTabChange}>
        <div className={cx(styles.htmlEditorHeader, 'handle', { [styles.htmlEditorFull]: state.fullView })}>
          {!state.fullView &&
            <Nav className={styles.htmlEditorTabs}>
              <Nav.Item>
                <Nav.Link eventKey="html">{renderTabsIcon('html')}</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="css">{renderTabsIcon('css')}</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="js">{renderTabsIcon('js')}</Nav.Link>
              </Nav.Item>
            </Nav>
          }

          {state.fullView &&
            <h2 className={styles.title}>Code Editor</h2>
          }

          <div className={styles.tabsButtonTray}>
            <Button variant="info" onClick={() => props.handleSetting()} ><i className={cx("cleverstory-settings", styles.settingsIcon)} /></Button>
            {state.fullView &&
              <OverlayTrigger
                trigger="click"
                placement="bottom"
                rootClose
                overlay={popover}
              >
                <Button variant="info" ><i className={'cleverstory-top-view'} /></Button>
              </OverlayTrigger>
            }
            <Button variant="info" onClick={toggleView} ><i className={'cleverstory-expand-editor'} /></Button>
            <Button variant="info" onClick={() => props.onCancel()} ><i className={'cleverstory-close'} /></Button>

          </div>
        </div>

        {!state.fullView &&
          <div className={styles.tabEditor}>
            <Tab.Content>
              {state.active === 'html' && <Tab.Pane eventKey="html">
                <CodeEditor value={_.get(widgetContent, 'html', '')} onChange={props.handleHtml} mode="html" />
              </Tab.Pane>}
              {state.active === 'css' && <Tab.Pane eventKey="css">
                <CodeEditor value={_.get(widgetContent, 'css', '')} onChange={props.handleCss} mode="css" />
              </Tab.Pane>}
              {state.active === 'js' && <Tab.Pane eventKey="js">
                <CodeEditor value={_.get(widgetContent, 'script', '')} onChange={props.handleJs} mode="javascript" />
              </Tab.Pane>}
            </Tab.Content>
          </div>
        }

        {state.fullView &&
          <HtmlExtendedEditor
            widget={props.widget}
            layout={state.layout}
            handleHtml={props.handleHtml}
            handleCss={props.handleCss}
            handleJs={props.handleJs}
          />
        }
      </TabContainer>
    </HtmlEditorDialog>
  );
}

const HtmlSettingDialog = (props) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const id = _.get(props, 'widget.uid');
  const widgetContent = _.get(props, `widget.content`);

  const [state, setState] = useState({
    active: 'css',
  })

  const renderTabsIcon = (title) => {
    return (
      <div className={styles.tabsButton}>
        <i className={`cleverstory-${title}`} />
        {state.active === title && <span className={styles.tabsTitle}>{title}</span>}
      </div>
    )
  }


  const handleTabChange = (active) => {
    setState(state => {
      return {
        ...state,
        active: active
      }
    })
  }


  const handleSaveChanges = (resourceData) => {
    dispatch(updateWidgetContent(editorSession, id,
      {
        ...widgetContent,
        resources: resourceData
      }
    ));
  }



  return (
    <HtmlEditorDialog
      width={500}
      height={395}
      className={styles.htmlSettingDialog}
      target={`#${id}`}
      onCancel={props.onClose}
    >
      <TabContainer id='html-widget-editor-tab' defaultActiveKey={state.active} onSelect={handleTabChange}>
        <div className={cx(styles.htmlSettingWrapper, 'handle')}>
          <div className={cx(styles.htmlSettingHeader)}>
            <h4 className={cx(styles.titleBox)}>Code Settings</h4>
            <p className={styles.description}>
              Any URL’s added here will be added as script's in order, and run before the JavaScript in the editor. You can use the URL of any other Pen and it will include the JavaScript from that Pen.
            </p>
            <Button className={styles.closeButton} variant="info" onClick={() => props.onClose()} ><i className={'cleverstory-close'} /></Button>
          </div>
          <Nav className={styles.htmlSettingsTabs}>
            <Nav.Item>
              <Nav.Link eventKey="css">{renderTabsIcon('css')}</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="js">{renderTabsIcon('js')}</Nav.Link>
            </Nav.Item>
          </Nav>
          <div className={styles.tabContainer}>
            <Tab.Content>
              <Tab.Pane eventKey="css">
                <ResourceEditor type="LINK" onCancel={props.onClose} onSave={handleSaveChanges} value={_.get(widgetContent, 'resources', '')} />
              </Tab.Pane>
              <Tab.Pane eventKey="js">
                <ResourceEditor type="SCRIPT" onCancel={props.onClose} onSave={handleSaveChanges} value={_.get(widgetContent, 'resources', '')} />
              </Tab.Pane>
            </Tab.Content>
          </div>
        </div>
      </TabContainer>
    </HtmlEditorDialog>
  )
}

const HtmlExtendedEditor = (props) => {
  const [state, setState] = useState({
    fullView: false
  });

  const id = _.get(props, 'widget.uid');
  const widgetContent = _.get(props, `widget.content`);
  const htmlTags = _.get(props, 'widget.content.html');
  var iframe = true // set true for maximised editor


  const toggleFullView = () => {
    setState(state => {
      return {
        ...state,
        fullView: !state.fullView
      }
    })
  }
  return (
    <div className={styles.extendedEditor}>
      <div className={cx(styles.extendedEditorInner, { [styles.fullHtmlView]: props.layout === 'horizontal' && state.fullView },
        { [styles.verticalRightLayout]: props.layout === 'vertical-right' }, { [styles.verticalLeftLayout]: props.layout === 'vertical-left' }, { [styles.verticalfullHtmlView]: (props.layout === 'vertical-right' || props.layout === 'vertical-left') && state.fullView })}>
        <div className={cx(styles.extendedEditorContainer, { [styles.editorVerticalLayout]: props.layout === 'vertical-right' || props.layout === 'vertical-left' })}>
          <table className={cx(styles.extendedEditorTable, { [styles.verticalExtendedEditorTable]: props.layout === 'vertical-right' || props.layout === 'vertical-left' })} cellpadding="0" cellspacing="0">
            <tbody>
              <tr>
                <td>
                  <div className={styles.codeHeader}>
                    <div className={styles.inner}>
                      <i className={`cleverstory-Embed`} />
                      <span className={styles.title}>HTML</span>
                    </div>
                  </div>
                  <CodeEditor
                    value={_.get(widgetContent, 'html', '')}
                    onChange={props.handleHtml}
                    mode="html"
                    height={props.layout === 'vertical-right' || props.layout === 'vertical-left' ? '200px' : 'calc(100% - 28px)'} />
                </td>

                <ColumnResizer disabled={props.layout === 'vertical-right' || props.layout === 'vertical-left'} className={styles.columnResizer} />

                <td>
                  <div className={styles.codeHeader}>
                    <div className={styles.inner}>
                      <i className={`cleverstory-css`} />
                      <span className={styles.title}>CSS</span>
                    </div>
                  </div>
                  <CodeEditor
                    value={_.get(widgetContent, 'css', '')}
                    onChange={props.handleCss}
                    mode="css"
                    height={props.layout === 'vertical-right' || props.layout === 'vertical-left' ? '200px' : 'calc(100% - 28px)'} />
                </td>

                <ColumnResizer disabled={props.layout === 'vertical-right' || props.layout === 'vertical-left'} className={styles.columnResizer} />

                <td>
                  <div className={styles.codeHeader}>
                    <div className={styles.inner}>
                      <i className={`cleverstory-js`} />
                      <span className={styles.title}>JS</span>
                    </div>
                  </div>
                  <CodeEditor
                    value={_.get(widgetContent, 'script', '')}
                    onChange={props.handleJs}
                    mode="javascript"
                    height={props.layout === 'vertical-right' || props.layout === 'vertical-left' ? '200px' : 'calc(100% - 28px)'} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className={cx(styles.htmlViewContainer, { [styles.viewerVerticalLayout]: props.layout === 'vertical-right' || props.layout === 'vertical-left' })}>
          <div className={styles.renderViewer}>
            {!htmlTags &&
              <div className={styles.htmlEmtyMessage}>
                <p className={styles.emptyMessage}>Please edit your custom html widget</p>
              </div>
            }
            {htmlTags &&
              <HtmlView content={widgetContent} iframe={iframe} />
            }
          </div>
          <div onClick={toggleFullView} className={cx(styles.fullViewButton,
            { [styles.leftViewButton]: props.layout === 'vertical-left' }, { [styles.verticalViewButton]: props.layout === 'vertical-right' })}>
            <i className={cx(styles.expandIcon, `cleverstory-drop-down`)} />
          </div>
        </div>
      </div>
    </div>
  )
}

const HtmlEditorDialog = React.forwardRef((props, ref) => {
  const idRef = React.useRef(`dialog_${shortid()}`);
  const defaultRef = React.useRef(null);
  const dialogRef = ref || defaultRef;

  const [position, setPosition] = useState(null);

  const dialogStyle = {
    width: props.width ? props.width : 300,
    height: props.height ? props.height : 400,
    visibility: position != null ? 'visible' : 'hidden'
  }

  const calculateDialogPosition = () => {
    const target = props.target;
    var targetEl = typeof target === 'string' ? document.querySelector(target) : target;

    if (!targetEl) {
      console.warn(`Target ${target} not found`);
      return;
    }

    if (!dialogRef.current) {
      console.warn(`Dialog ref unmounted`);
      return;
    }

    const width = props.width;
    const height = dialogRef.current.getBoundingClientRect().height;

    const rect = targetEl.getBoundingClientRect();

    const minLeft = 30;
    const maxleft = window.innerWidth - rect.width - 30;

    const minTop = 30;
    const maxTop = window.innerHeight - height - 20;

    var left = rect.left - width - 20;
    if (width > rect.left - 30) {
      left = rect.left + rect.width + 20;
    }

    left = Math.max(minLeft, left);
    left = Math.min(maxleft, left);

    var top = rect.top;
    top = Math.max(minTop, top);
    top = Math.min(maxTop, top)

    setPosition({
      x: left,
      y: top
    })
  }

  const handlePositionChange = (e, ui) => {
    setPosition((position) => {
      return {
        x: position.x + ui.deltaX,
        y: position.y + + ui.deltaY
      };
    });
  }

  const attachResizeObserver = () => {
    if (!dialogRef.current) {
      console.warn(`Target not found`);
      return;
    }

    const resizeObserver = new ResizeObserver(entries => {
      calculateDialogPosition();
    });

    resizeObserver.observe(dialogRef.current);
  }

  useEffect(() => {
    attachResizeObserver();
    calculateDialogPosition();
  }, []);


  return (
    <Portal>
      {props.modal &&
        <div className={cx(styles.overlay, props.overlayClassName)} onClick={props.onCancel}></div>
      }
      <Draggable handle={`#${idRef.current} .handle`} bounds='parent' position={position} onDrag={handlePositionChange}>
        <div id={idRef.current} ref={dialogRef} className={cx(styles.dialog, { [styles.dialogFull]: props.fullView })}
          style={!props.fullView ? dialogStyle : { transform: 'none' }}>
          {props.children}
        </div>
      </Draggable>
    </Portal>
  );
});

HtmlWidget.moveableConfig = (editorSession, widget) => {
  var id = widget && widget.uid ? widget.uid : null;
  const group = getParentContainerOfType(editorSession, id, GROUP);

  return {
    draggable: true,
    resizable: true,
    rotatable: true,
    renderDirections: ["s", "se", "sw", "e", "w"],
    onReparent: (widget, parent) => {
      var parentElement = parent ? document.getElementById(parent) : null;
      if (parentElement && parentElement.getAttribute('data-widget-type') === HOTSPOT) {
        return false;
      }

      const hasHotspotChild = isWidgetHasHotspotChild(editorSession, widget);
      const parentType = getWidgetType(editorSession, parent);

      const denyHostpotReparent = hasHotspotChild && ![PAGE, SECTION_COLUMN, CONTAINER].includes(parentType);

      if (group || denyHostpotReparent) {
        return false;
      }

      return true;
    }
  }
}

HtmlWidget.actions = (editor, breakpoint, props) => {
  return (
    <>
      {breakpoint === 'desktop' &&
        <HtmlWidgetDesktopAction editorSession={editor} breakpoint={breakpoint} {...props} />
      }

      {breakpoint === 'mobile' &&
        <HtmlWidgetDesktopAction editorSession={editor} breakpoint={breakpoint} {...props} />
      }
    </>
  );
}

HtmlWidget.processRequest = (widget) => {
  return widget;
}

export { HtmlWidget };
